import { useState, useEffect, useCallback } from "react";
import { Download, X, Share, PlusSquare } from "lucide-react";

// UI preference only (non-sensitive): remembers that the user dismissed the
// "Install app" banner so we don't show it again. No personal/sensitive data
// is ever stored here — safe to keep in localStorage.
const DISMISS_KEY = "petalfinder-install-dismissed";

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const isIos = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) &&
  !window.MSStream;

const isSafari = () =>
  /^((?!chrome|android|crios|fxios).)*safari/i.test(window.navigator.userAgent);

/**
 * InstallPWA renders an "Install app" affordance.
 * - Android/desktop Chrome/Edge: uses the captured `beforeinstallprompt` event.
 * - iOS Safari: shows manual "Add to Home Screen" instructions (no native prompt).
 * Pass `t` (translation dict). `variant="button"` renders a compact header button,
 * default renders a floating bottom banner.
 */
export const InstallPWA = ({ t, variant = "banner" }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY) === "1") return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const installedHandler = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", installedHandler);

    // iOS Safari never fires beforeinstallprompt — show manual hint.
    if (isIos() && isSafari()) {
      setVisible(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setVisible(false);
      }
      setDeferredPrompt(null);
    } else if (isIos()) {
      setShowIosHint(true);
    }
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    setVisible(false);
    setShowIosHint(false);
    localStorage.setItem(DISMISS_KEY, "1");
  }, []);

  if (!visible) return null;

  // Compact header button
  if (variant === "button") {
    return (
      <button
        onClick={handleInstall}
        data-testid="install-app-button"
        className="flex items-center gap-2 px-4 py-2 rounded-full transition-all hover:scale-105"
        style={{ backgroundColor: "#1A3C34", color: "#FFFFFF" }}
      >
        <Download className="w-4 h-4" />
        <span className="font-body text-sm">{t.installApp}</span>
      </button>
    );
  }

  // Floating bottom banner
  return (
    <div
      data-testid="install-banner"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[92%] max-w-md"
    >
      <div
        className="rounded-2xl shadow-2xl p-4 flex items-start gap-3 backdrop-blur"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.97)",
          border: "1px solid rgba(163, 177, 138, 0.4)",
        }}
      >
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: "rgba(163, 177, 138, 0.2)" }}
        >
          <Download className="w-5 h-5" style={{ color: "#1A3C34" }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading text-base" style={{ color: "#1A3C34" }}>
            {t.installTitle}
          </p>
          {showIosHint ? (
            <p
              className="font-body text-sm mt-1 flex items-center flex-wrap gap-1"
              style={{ color: "#6B705C" }}
              data-testid="ios-install-hint"
            >
              <Share className="w-4 h-4 inline" />
              <PlusSquare className="w-4 h-4 inline" />
              <span>{t.iosInstallHint}</span>
            </p>
          ) : (
            <p className="font-body text-sm mt-1" style={{ color: "#6B705C" }}>
              {t.installDesc}
            </p>
          )}
          {!showIosHint && (
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={handleInstall}
                data-testid="install-now-button"
                className="px-4 py-2 rounded-full font-body text-sm transition-all hover:scale-105"
                style={{ backgroundColor: "#1A3C34", color: "#FFFFFF" }}
              >
                {t.installNow}
              </button>
              <button
                onClick={handleDismiss}
                data-testid="install-later-button"
                className="px-4 py-2 rounded-full font-body text-sm transition-all"
                style={{ color: "#6B705C" }}
              >
                {t.installLater}
              </button>
            </div>
          )}
        </div>
        <button
          onClick={handleDismiss}
          data-testid="install-dismiss-button"
          className="shrink-0 p-1 rounded-full transition-all hover:bg-black/5"
          aria-label={t.installLater}
        >
          <X className="w-4 h-4" style={{ color: "#6B705C" }} />
        </button>
      </div>
    </div>
  );
};
